import { Clock3, Radio, Rocket } from "lucide-react";
import { Link } from "react-router";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { cn } from "@ncp/ui";
import type { LiveView, PendingRelease } from "../types";

/**
 * Always-visible answer to "what is live?": the live version, its commit and release, running cases, and how the version on screen
 * relates to it.
 */
export function LiveBanner({ live, liveHash, pending, relation, onShowLive, className }: {
  live?: LiveView | null; liveHash?: string | null; pending: PendingRelease[]; relation: { kind: "live" | "ahead" | "behind" | "diverged" | "none"; ahead?: number; behind?: number }; onShowLive?: () => void; className?: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const release = pending[0];
  return (
    <div className={cn("flex min-w-0 items-center gap-3 text-xs", className)}>
      {live ? (
        <button type="button" onClick={onShowLive} className="group flex min-w-0 items-center gap-2 rounded-full border border-success-200 bg-success-50 py-1 pe-3 ps-1 text-success-900 transition hover:border-success-400" title={live.commitMessage ?? undefined}>
          <span className="relative grid size-6 place-items-center rounded-full bg-success-600 text-white"><Radio className="size-3.5" /><span className="absolute inset-0 animate-ping rounded-full bg-success-500/40" /></span>
          <span className="font-semibold">{t("admin:builder.live.live", { version: live.version })}</span>
          {liveHash ? <span className="font-mono text-[10px] opacity-70">{liveHash}</span> : null}
          <span className="hidden truncate text-[11px] opacity-80 xl:inline">{live.publishedAt ? t("admin:builder.live.published", { date: formatDateTime(live.publishedAt, locale), by: live.approvedBy ?? live.publishedBy ?? "—" }) : null}{live.releaseTitle ? ` · ${live.releaseTitle}` : ""}</span>
          <span className="rounded-full bg-white/70 px-1.5 font-semibold tabular-nums">{t("admin:builder.live.running", { count: live.runningCases })}</span>
        </button>
      ) : (
        <span className="rounded-full border border-sand-300 bg-sand-50 px-3 py-1 text-muted-foreground">{t("admin:builder.live.notLive")}</span>
      )}
      <span className="hidden shrink-0 text-muted-foreground lg:inline">
        {relation.kind === "live" ? t("admin:builder.live.viewingLive") : relation.kind === "ahead" ? t("admin:builder.live.ahead", { count: relation.ahead }) : relation.kind === "behind" ? t("admin:builder.live.behind", { count: relation.behind }) : relation.kind === "diverged" ? t("admin:builder.live.diverged", { ahead: relation.ahead, behind: relation.behind }) : null}
      </span>
      {release ? (
        <Link to={`/releases/${release.changeSetId}`} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-warning-200 bg-warning-50 px-2.5 py-1 text-warning-800 hover:border-warning-400">
          {release.status === "Draft" ? <Rocket className="size-3.5" /> : <Clock3 className="size-3.5" />}
          {t("admin:builder.live.pending", { version: release.version ?? "?", status: t(`admin:builder.live.releaseStatus.${release.status}`, { defaultValue: release.status }) })}
        </Link>
      ) : null}
    </div>
  );
}
